// BusinessReportsPage.jsx - Main Business Reports Container with Categorized Navigation
import React, { useContext, useState } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";

// Financial Reports
import CostAnalysisReport from "./CostAnalysisReport";
import InvoiceAgingReport from "./InvoiceAgingReport";
import RevenueForecastReport from "./RevenueForecastReport";
import JobCoachProfitabilityReport from "./JobCoachProfitabilityReport";

// Operational Reports
import AuthorizationUtilizationReport from "./AuthorizationUtilizationReport";
import ServiceDeliveryReport from "./ServiceDeliveryReport";
import ConsumerActivityReport from "./ConsumerActivityReport";
import MonthlyLiaisonReport from "./MonthlyLiaisonReport";
import TravelAreaAnalysisReport from "./TravelAreaAnalysisReport";

// Compliance Reports
import MonthlyReportComplianceReport from "./MonthlyReportComplianceReport";
import DocumentationComplianceReport from "./DocumentationComplianceReport";

// Administrative Reports (Primary Users Only)
import TenantUserCountReport from "./TenantUserCountReport";

// Report Information Modal Component
const ReportInfoModal = ({ report, onClose }) => {
  if (!report) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className={`modal-header bg-${report.color} bg-opacity-10`}>
            <h5 className="modal-title">
              <i className={`bi ${report.icon} me-2`}></i>
              {report.name}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-4">
              <h6 className="text-muted mb-2">
                <i className="bi bi-info-circle me-2"></i>Description
              </h6>
              <p>{report.description}</p>
            </div>

            <div className="mb-4">
              <h6 className="text-muted mb-2">
                <i className="bi bi-database me-2"></i>Data Sources
              </h6>
              <ul className="list-unstyled">
                {report.dataSources.map((source, index) => (
                  <li key={index} className="mb-1">
                    <i className="bi bi-dot text-primary"></i>
                    <strong>{source.table}:</strong> {source.description}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4">
              <h6 className="text-muted mb-2">
                <i className="bi bi-calculator me-2"></i>How It's Calculated
              </h6>
              <div className="bg-light p-3 rounded">
                {report.calculations.map((calc, index) => (
                  <div key={index} className="mb-2">
                    <strong className="text-primary">{calc.metric}:</strong>
                    <br />
                    <code className="small">{calc.formula}</code>
                    {calc.note && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-info-circle me-1"></i>
                        {calc.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {report.filters && (
              <div className="mb-4">
                <h6 className="text-muted mb-2">
                  <i className="bi bi-funnel me-2"></i>Available Filters
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {report.filters.map((filter, index) => (
                    <span key={index} className="badge bg-secondary">
                      {filter}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {report.tips && (
              <div className="alert alert-info mb-0">
                <h6 className="alert-heading">
                  <i className="bi bi-lightbulb me-2"></i>Tips
                </h6>
                <ul className="mb-0 small">
                  {report.tips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BusinessReportsPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [expandedCategory, setExpandedCategory] = useState("financial");
  const [selectedReportInfo, setSelectedReportInfo] = useState(null);

  // Check if user has admin role
  const isAdmin = user?.is_admin === true;
  const isVendor = user?.tenant_type?.toLowerCase() === "vendor";
  const isPrimary = user?.is_primary === true;
  
  // Check if invoice enhancements are enabled for this tenant
  const invoiceEnhancementsEnabled = user?.enable_invoices === true;

  // Report descriptions with data sources and calculations
  const reportInfo = {
    "cost-analysis": {
      name: "Cost Analysis",
      icon: "bi-graph-up",
      color: "success",
      description:
        "Comprehensive profitability analysis that compares your labor and mileage costs against revenue generated from service delivery. This report starts from authorizations as the source of truth, then aggregates job logs and mileage to calculate actual costs and revenue. Supports multiple grouping options for different analysis perspectives.",
      dataSources: [
        {
          table: "auths",
          description:
            "Authorization records including hours approved, service type, flat rate flags, and completion status",
        },
        {
          table: "job_logs",
          description:
            "Actual hours worked by job coaches on each authorization, with payment and invoice status",
        },
        {
          table: "mileage_logs",
          description: "Travel miles logged against consumers with payment and invoice status",
        },
        {
          table: "users",
          description: "Job coach hourly rates and mileage rates for cost calculation",
        },
        {
          table: "service",
          description: "Service billing rates (date-effective) and flat rate indicators",
        },
        {
          table: "service_rates",
          description: "Historical service rates for accurate date-based billing calculations",
        },
      ],
      calculations: [
        {
          metric: "Service Cost",
          formula: "SUM(hours_worked x user_hourly_rate)",
          note: "Based on each job coach's configured hourly rate at time of service",
        },
        {
          metric: "Mileage Cost",
          formula: "SUM(total_miles x user_mileage_rate)",
          note: "Default mileage rate is $0.67/mile if not configured",
        },
        {
          metric: "Cost Paid / Cost Unpaid",
          formula: "Cost breakdown by payment status from job logs and mileage logs",
          note: "Green checkmark = paid to employees; Yellow clock = not yet paid",
        },
        {
          metric: "Revenue (Hourly Services)",
          formula: "SUM(hours_worked x date_effective_service_rate)",
          note: "Uses the service rate that was effective on the date of service",
        },
        {
          metric: "Revenue (Flat Rate Services)",
          formula: "authorization_hours x service_rate",
          note: "Flat rate services bill at authorized hours regardless of actual hours worked",
        },
        {
          metric: "Revenue Invoiced / Not Invoiced",
          formula: "Revenue breakdown by invoice status from job logs",
          note: "Green receipt = invoiced to customer; Yellow hourglass = not yet invoiced",
        },
        {
          metric: "Profit",
          formula: "Total Revenue - Total Cost",
        },
        {
          metric: "Margin %",
          formula: "(Profit / Revenue) x 100",
        },
        {
          metric: "Auth Status",
          formula: "Complete (checkmark) / In Progress (circle) count",
          note: "Shows how many authorizations are complete vs. still in progress",
        },
      ],
      filters: [
        "Customer",
        "Consumer",
        "Job Coach",
        "Service",
        "Date Range",
        "Group By (Authorization, Consumer, Customer, Job Coach, Service, Month)",
      ],
      tips: [
        "Use 'Group By' to analyze profitability at different levels",
        "Negative margins indicate services being delivered at a loss",
        "Compare flat rate vs hourly services to optimize pricing strategy",
        "Cost Paid/Unpaid shows your cash flow obligations to job coaches",
        "Revenue Invoiced/Not Invoiced identifies billing opportunities",
        "Hover over sub-amounts to see what each color represents",
      ],
    },
    "invoice-aging": {
      name: "Invoice Aging",
      icon: "bi-clock-history",
      color: "success",
      description:
        "Accounts receivable aging report showing outstanding invoice balances categorized by how long they've been past due. Helps identify collection priorities and cash flow issues. Click on aging bucket cards to filter invoices by age category.",
      dataSources: [
        {
          table: "invoice",
          description:
            "Invoice records including amounts, dates, payment status, and partial payments",
        },
        {
          table: "customers",
          description: "Customer names for grouping and identification",
        },
      ],
      calculations: [
        {
          metric: "Balance Due",
          formula: "total_amount - amount_paid",
          note: "Only invoices with balance > 0 are included",
        },
        {
          metric: "Days Overdue",
          formula: "Current Date - Due Date (or Invoice Date if no due date)",
        },
        {
          metric: "Aging Buckets",
          formula:
            "Current (not due), 1-30 days, 31-60 days, 61-90 days, 90+ days",
        },
        {
          metric: "Total Outstanding",
          formula: "SUM of all unpaid invoice balances",
        },
      ],
      filters: ["Customer", "Aging Bucket (clickable summary cards)"],
      tips: [
        "Focus collection efforts on 90+ day balances first",
        "Click on aging summary cards to filter invoices by category",
        "Use the Record Payment button to track partial or full payments",
        "High balances in older buckets may indicate collection problems",
      ],
    },
    "revenue-forecast": {
      name: "Revenue Forecast",
      icon: "bi-graph-up-arrow",
      color: "success",
      description:
        "Projects potential revenue from open authorizations based on remaining authorized hours and service rates. Tracks unbilled hours (worked but not yet invoiced) to give a complete revenue pipeline view. Helps with capacity planning, billing priorities, and revenue projections.",
      dataSources: [
        {
          table: "auths",
          description:
            "Open (incomplete) authorizations with remaining hours available",
        },
        {
          table: "job_logs",
          description: "Hours already used against each authorization, including invoice status",
        },
        {
          table: "service",
          description: "Service billing rates for revenue calculation",
        },
        {
          table: "consumers",
          description: "Consumer information for grouping",
        },
        {
          table: "customers",
          description: "Customer information for grouping",
        },
      ],
      calculations: [
        {
          metric: "Auth Hours",
          formula: "Total authorized hours from authorization records",
        },
        {
          metric: "Hours Used",
          formula: "SUM(hours_worked) from job_logs per authorization",
        },
        {
          metric: "Hours Unbilled",
          formula: "SUM(hours_worked) WHERE is_invoiced = false AND is_billable = true",
          note: "Hours worked but not yet added to an invoice",
        },
        {
          metric: "Hours Remaining",
          formula: "authorization_hours - hours_used",
          note: "Only positive remaining hours are included",
        },
        {
          metric: "Rate",
          formula: "Current service rate (shows 'Varies' when grouped across services)",
        },
        {
          metric: "Unbilled Revenue",
          formula: "hours_unbilled x service_rate",
          note: "Revenue that can be immediately invoiced (shown in red)",
        },
        {
          metric: "Projected Revenue",
          formula: "hours_remaining x service_rate",
          note: "Revenue from hours not yet worked",
        },
      ],
      filters: ["Customer", "Group By (Customer, Consumer, Service, Authorization)"],
      tips: [
        "Unbilled Revenue (red) shows money you can invoice immediately",
        "Projected Revenue shows future potential from remaining hours",
        "Expand grouped rows to see individual authorization details",
        "'Varies' rate indicates multiple services with different rates",
      ],
    },
    "job-coach-profitability": {
      name: "Job Coach Profitability",
      icon: "bi-person-badge",
      color: "success",
      description:
        "Analyzes profitability by job coach with hierarchical drill-down from Job Coach to Consumer to Authorization level. Shows revenue generated versus labor and mileage costs, with payment and invoice status breakdowns. Includes all authorizations, even those with no activity yet.",
      dataSources: [
        {
          table: "job_logs",
          description: "Hours worked by each job coach with payment and invoice status",
        },
        {
          table: "mileage_logs",
          description: "Miles traveled by each job coach with payment and invoice status",
        },
        {
          table: "users",
          description:
            "Job coach names, hourly rates, and mileage rates",
        },
        {
          table: "auths",
          description: "All authorizations including those with no activity",
        },
        {
          table: "service",
          description: "Service billing rates (date-effective)",
        },
        {
          table: "service_rates",
          description: "Historical service rates for accurate calculations",
        },
        {
          table: "job_coach_assignments",
          description: "Links job coaches to consumers for zero-activity authorizations",
        },
      ],
      calculations: [
        {
          metric: "Hours",
          formula: "SUM(hours_worked) per job coach/consumer/authorization",
        },
        {
          metric: "Mileage",
          formula: "SUM(total_miles) per job coach/consumer",
          note: "Mileage is tracked at the consumer level, not authorization",
        },
        {
          metric: "Hourly Rate",
          formula: "Job coach's configured hourly pay rate",
        },
        {
          metric: "Service Cost",
          formula: "SUM(hours x user_hourly_rate)",
        },
        {
          metric: "Mileage Cost",
          formula: "SUM(miles x user_mileage_rate)",
        },
        {
          metric: "Total Cost (Paid/Unpaid)",
          formula: "Service Cost + Mileage Cost with payment breakdown",
          note: "Green checkmark = paid to employees; Yellow clock = not yet paid",
        },
        {
          metric: "Revenue (Invoiced/Not Invoiced)",
          formula: "SUM(hours x date_effective_service_rate)",
          note: "Green receipt = invoiced to customer; Yellow receipt = not yet invoiced",
        },
        {
          metric: "Profit",
          formula: "Revenue - Total Cost",
        },
        {
          metric: "Margin %",
          formula: "(Profit / Revenue) x 100",
          note: "Green (30%+), Blue (15-30%), Yellow (0-15%), Red (negative)",
        },
      ],
      filters: ["Date Range", "Job Coach"],
      tips: [
        "Click job coach rows to expand and see consumer-level details",
        "Click consumer rows to expand and see authorization-level details",
        "Authorizations with no activity are included (shows $0 values)",
        "Hover over cost/revenue sub-amounts to see what each color represents",
        "Summary cards at top show totals with paid/unpaid and invoiced/not-invoiced breakdowns",
      ],
    },
    "auth-utilization": {
      name: "Authorization Utilization",
      icon: "bi-pie-chart",
      color: "primary",
      description:
        "Shows how much of each authorization's approved hours have been used. Identifies over-utilized authorizations needing renewal and underutilized ones needing attention. Flat rate services show Complete/Unused status instead of percentage.",
      dataSources: [
        {
          table: "auths",
          description: "Authorization hours, status, and flat rate flags",
        },
        {
          table: "job_logs",
          description: "Actual hours worked against each authorization",
        },
        {
          table: "consumers",
          description: "Consumer names and associations",
        },
        {
          table: "customers",
          description: "Customer names for filtering",
        },
        {
          table: "service",
          description: "Service names and flat rate indicators",
        },
      ],
      calculations: [
        {
          metric: "Auth Hours",
          formula: "Authorized hours from authorization record",
        },
        {
          metric: "Hours Used",
          formula: "SUM(hours_worked) from job_logs",
        },
        {
          metric: "Hours Remaining",
          formula: "authorization_hours - hours_used",
        },
        {
          metric: "Utilization %",
          formula: "(hours_used / authorization_hours) x 100",
        },
        {
          metric: "Status Categories (Hourly)",
          formula:
            "Over Limit (100%+), Complete (100%), Approaching Limit (80-99%), Low Usage (<50%), Unused (0%)",
        },
        {
          metric: "Status Categories (Flat Rate)",
          formula:
            "Complete (any work done), Unused (no work done)",
          note: "Flat rate services show simplified status since billing is not hour-based",
        },
      ],
      filters: ["Customer", "Status Filter", "Sort By (Utilization High/Low, Hours Remaining)"],
      tips: [
        "Over-utilized authorizations may need additional hours requested",
        "Unused authorizations might indicate barriers to service delivery",
        "Approaching limit authorizations need proactive renewal planning",
        "Flat rate services are marked with a badge and use simplified status",
      ],
    },
    "service-delivery": {
      name: "Service Delivery",
      icon: "bi-list-check",
      color: "primary",
      description:
        "Analyzes service delivery patterns including total hours, billable vs non-billable breakdown, and monthly trends. Visual distribution bar shows billable/non-billable proportion. Helps optimize service mix and identify non-billable work.",
      dataSources: [
        {
          table: "job_logs",
          description:
            "Service hours with billable flags and service dates",
        },
        {
          table: "auths",
          description: "Links job logs to services",
        },
        {
          table: "service",
          description: "Service names and billing rates",
        },
        {
          table: "customers",
          description: "Customer information for grouping",
        },
      ],
      calculations: [
        {
          metric: "Total Hours",
          formula: "SUM(hours_worked) from job_logs",
        },
        {
          metric: "Billable Hours",
          formula: "SUM(hours_worked) WHERE is_billable = true",
        },
        {
          metric: "Non-Billable Hours",
          formula: "SUM(hours_worked) WHERE is_billable = false",
        },
        {
          metric: "Billable %",
          formula: "(billable_hours / total_hours) x 100",
          note: "Green (80%+), Yellow (50-79%), Red (<50%)",
        },
        {
          metric: "Distribution Bar",
          formula: "Visual bar showing billable (green) vs non-billable (gray)",
        },
        {
          metric: "Revenue",
          formula: "SUM(billable_hours x service_rate)",
        },
        {
          metric: "Monthly Trend",
          formula: "Hours and Revenue by month with visual trend bar",
        },
      ],
      filters: ["Customer", "Date Range", "Group By (Service, Customer, Month)"],
      tips: [
        "High non-billable percentages may indicate process inefficiencies",
        "Use monthly trends to identify seasonal patterns",
        "The distribution bar provides quick visual comparison across rows",
      ],
    },
    "consumer-activity": {
      name: "Consumer Activity",
      icon: "bi-people",
      color: "primary",
      description:
        "Monitors consumer engagement by tracking days since last service. Identifies at-risk consumers who may be disengaging and helps maintain active caseloads. Color-coded status badges enable quick identification of consumers needing attention.",
      dataSources: [
        {
          table: "consumers",
          description: "Consumer records, status, and assignments",
        },
        {
          table: "job_logs",
          description: "Service dates to calculate last activity",
        },
        {
          table: "auths",
          description: "Active authorization counts",
        },
        {
          table: "customers",
          description: "Customer associations",
        },
      ],
      calculations: [
        {
          metric: "Last Service Date",
          formula: "MAX(date_of_service) from job_logs",
        },
        {
          metric: "Days Since Last Service",
          formula: "Current Date - Last Service Date",
        },
        {
          metric: "Activity Status",
          formula:
            "Active (0-30 days), At Risk (31-60 days), Urgent (61-90 days), Inactive (90+ days), No Activity (never served)",
          note: "Color-coded: Green, Yellow, Orange, Red, Gray",
        },
        {
          metric: "Total Hours",
          formula: "SUM(hours_worked) all time for consumer",
        },
        {
          metric: "Hours This Month",
          formula: "SUM(hours_worked) for current month",
        },
        {
          metric: "Active Auths",
          formula: "Count of open (incomplete) authorizations",
        },
      ],
      filters: ["Customer", "Activity Status Filter"],
      tips: [
        "Prioritize outreach to 'At Risk' and 'Urgent' consumers",
        "Consumers with active auths but no recent service may have barriers",
        "'No Activity' indicates consumers who have never received service",
        "Use summary cards for quick overview of engagement health",
      ],
    },
    "monthly-liaison": {
      name: "Monthly Liaison Report",
      icon: "bi-clipboard-data",
      color: "primary",
      description:
        "Comprehensive monthly overview designed for liaison meetings and stakeholder updates. Shows consumer service counts, service distribution by branch and counselor, and referral pipeline progress. Toggle between summary and detailed views.",
      dataSources: [
        {
          table: "consumers",
          description: "Consumer records with status, counselor, and branch information",
        },
        {
          table: "counselors",
          description: "Counselor names and branch assignments",
        },
        {
          table: "auths",
          description: "Active authorizations linking consumers to services",
        },
        {
          table: "job_logs",
          description: "Service hours delivered during the selected month",
        },
        {
          table: "mileage_logs",
          description: "Travel miles logged during the selected month",
        },
        {
          table: "service",
          description: "Service types and names",
        },
        {
          table: "job_coach_assignments",
          description: "Job coach to consumer assignments with hours and miles",
        },
        {
          table: "customers",
          description: "Customer groupings for filtering",
        },
      ],
      calculations: [
        {
          metric: "Total Consumers",
          formula: "COUNT of all consumers for the tenant/customer",
        },
        {
          metric: "Active This Month",
          formula: "Consumers with job_logs during the selected month",
        },
        {
          metric: "New Referrals",
          formula: "Consumers created during the selected month",
        },
        {
          metric: "Consumers by Status",
          formula: "GROUP BY consumer status with percentage breakdown",
        },
        {
          metric: "By Branch & Counselor",
          formula: "Consumer and authorization counts grouped by branch, then counselor",
          note: "Toggle 'With Services' to see service breakdown per counselor",
        },
        {
          metric: "Services Being Received",
          formula: "Services with active authorizations, showing hours this month and all-time",
        },
        {
          metric: "Referral Pipeline",
          formula: "New this month, Pending Assessment (no auth), Started Services",
          note: "Tracks consumer journey from referral to active services",
        },
        {
          metric: "Job Coach Activity",
          formula: "Assigned consumers, active auths, hours and miles per job coach",
        },
      ],
      filters: ["Report Month", "Customer"],
      tips: [
        "Use this report for monthly liaison meetings with customers",
        "Click section headers to expand/collapse details",
        "Toggle 'With Services' in Branch & Counselor view for service breakdown",
        "Referral Pipeline shows intake workflow efficiency",
        "Recent Referrals table shows new consumers this month",
      ],
    },
    "travel-area-analysis": {
      name: "Travel Area Analysis",
      icon: "bi-geo-alt",
      color: "primary",
      description:
        "Identifies geographic areas with the highest mileage activity by aggregating travel data based on consumer city addresses. Helps optimize service delivery routes, identify regional cost patterns, and understand job coach coverage across areas.",
      dataSources: [
        {
          table: "mileage_logs",
          description: "Trip records with miles, dates, and billable status",
        },
        {
          table: "addr",
          description: "Consumer addresses providing city, state, and postal code for geographic grouping",
        },
        {
          table: "consumers",
          description: "Consumer names linked to mileage activity",
        },
        {
          table: "users",
          description: "Job coach names and mileage rates for cost calculation",
        },
        {
          table: "service / service_rates",
          description: "Date-effective service rates for revenue calculation",
        },
      ],
      calculations: [
        {
          metric: "Total Miles",
          formula: "SUM(total_miles) per consumer city",
          note: "Only billable mileage with a consumer address is included in area breakdown",
        },
        {
          metric: "Travel Cost",
          formula: "SUM(miles x user_mileage_rate)",
          note: "Uses amount_paid if already paid, otherwise calculates from rate",
        },
        {
          metric: "Mileage Revenue",
          formula: "SUM(miles x date_effective_service_rate)",
          note: "Uses the service rate effective on the date of travel",
        },
        {
          metric: "Profit & Margin",
          formula: "Revenue - Cost; (Profit / Revenue) x 100",
        },
        {
          metric: "Avg Miles/Trip",
          formula: "Total Miles / Trip Count per area",
        },
        {
          metric: "Non-Billable Travel",
          formula: "Separate summary of non-billable miles and estimated cost",
          note: "Shown as a notice banner, not included in area breakdown",
        },
      ],
      filters: ["Customer", "Job Coach", "Date Range", "Group By (City, Postal Code)"],
      tips: [
        "Areas are ranked by total miles - the highest-travel areas appear first",
        "Intensity badges (red/yellow/blue) show relative travel volume",
        "Click any area row to expand and see consumer and job coach detail",
        "Use the Job Coach filter to see which areas a specific coach covers",
        "Hover over Cost and Revenue values to see paid/invoiced breakdown",
        "Non-billable travel is shown separately and not included in area totals",
        "Switch to Postal Code grouping for more granular geographic analysis",
      ],
    },
    "monthly-report-compliance": {
      name: "Monthly Report Compliance",
      icon: "bi-calendar-check",
      color: "warning",
      description:
        "Tracks monthly progress report submission compliance with hierarchical drill-down. View by Month, then Job Coach, then Consumer, then Authorization level. Identifies which job coaches have submitted required reports and flags missing or late submissions.",
      dataSources: [
        {
          table: "consumers",
          description: "Active consumers requiring monthly reports",
        },
        {
          table: "auths",
          description: "Active authorizations determining report requirements",
        },
        {
          table: "job_coach_assignments",
          description: "Job coach to consumer assignments",
        },
        {
          table: "monthly_reports",
          description: "Submitted monthly progress reports with dates",
        },
        {
          table: "customers",
          description: "Customer groupings for filtering",
        },
      ],
      calculations: [
        {
          metric: "Expected Reports",
          formula:
            "Count of active authorizations requiring monthly reports",
        },
        {
          metric: "Submitted",
          formula: "Count of monthly_reports submitted on time (green badge)",
        },
        {
          metric: "Late",
          formula: "Reports submitted after month end (yellow badge)",
        },
        {
          metric: "Missing",
          formula: "Expected - Submitted - Late (red badge)",
        },
        {
          metric: "Compliance Rate",
          formula: "(Submitted / Expected) x 100",
          note: "Green (90%+), Yellow (70-89%), Red (<70%)",
        },
        {
          metric: "Days Late",
          formula: "Days between submission date and month end",
          note: "Shown for late submissions at authorization level",
        },
        {
          metric: "Hours Used / Auth Hours",
          formula: "Progress tracking at authorization level",
        },
      ],
      filters: ["Customer", "Job Coach", "Status Filter", "Months Back"],
      tips: [
        "Review missing reports early in the month",
        "Low compliance rates may indicate training needs",
        "Drill down from Month to Job Coach to Consumer to Authorization",
        "Badge counts show submitted/late/missing at each level",
      ],
    },
    "documentation-compliance": {
      name: "Documentation Compliance",
      icon: "bi-file-earmark-check",
      color: "warning",
      description:
        "Identifies documentation issues including plans without matching authorizations. Organized by Month, then Consumer, then Plan for easy navigation. Ensures regulatory compliance and billing accuracy by flagging plans that cannot be billed.",
      dataSources: [
        {
          table: "plans",
          description:
            "Service plans checked for matching authorizations",
        },
        {
          table: "auths",
          description:
            "Authorization records to match against plans",
        },
        {
          table: "consumers",
          description: "Consumer associations",
        },
        {
          table: "customers",
          description: "Customer groupings for filtering",
        },
      ],
      calculations: [
        {
          metric: "Plans Without Auth",
          formula:
            "Plans WHERE no open auth exists for consumer at plan date",
        },
        {
          metric: "Expired Auths",
          formula:
            "Count of expired authorizations (summary only)",
        },
        {
          metric: "Consumers Affected",
          formula: "Distinct count of consumers with compliance issues",
        },
        {
          metric: "Total Issues",
          formula: "Plans Without Auth + Expired Auths",
        },
      ],
      filters: ["Customer", "Date Range (Start/End)"],
      tips: [
        "Plans without authorizations cannot be billed",
        "Expand months to see affected consumers",
        "Expand consumers to see individual plans needing authorization",
        "Use Expand All/Collapse All buttons for quick navigation",
      ],
    },
    "tenant-user-count": {
      name: "Tenant User Count",
      icon: "bi-people",
      color: "info",
      description:
        "Administrative report showing user counts across all tenants in the system. Only available to primary administrators. Provides visibility into user distribution, role breakdown, and active/inactive status across all organizations.",
      dataSources: [
        {
          table: "tenants",
          description: "All tenants/organizations in the system",
        },
        {
          table: "users",
          description: "User accounts with tenant assignments and active status",
        },
        {
          table: "roles",
          description: "User role assignments for admin count and role breakdown",
        },
      ],
      calculations: [
        {
          metric: "Total Users",
          formula: "COUNT of all users per tenant",
        },
        {
          metric: "Active Users",
          formula: "COUNT of users WHERE is_active = true",
        },
        {
          metric: "Inactive Users",
          formula: "COUNT of users WHERE is_active = false",
        },
        {
          metric: "Admin Count",
          formula: "COUNT of users WHERE role = 'Admin'",
        },
        {
          metric: "Avg Users/Tenant",
          formula: "Total Users / Total Tenants",
        },
        {
          metric: "Roles Breakdown",
          formula: "COUNT of users GROUP BY role per tenant",
        },
      ],
      filters: ["Tenant Type (Vendor/Customer)", "Include Inactive Users"],
      tips: [
        "Click column headers to sort by that field",
        "Use tenant type filter to focus on vendors or customers",
        "Enable 'Include Inactive Users' to see full user history",
        "Primary tenant is highlighted with a badge",
      ],
    },
  };

  // Report categories with their reports
  // Invoice Aging is only shown if invoice enhancements are enabled
  const reportCategories = [
    {
      id: "financial",
      name: "Financial Reports",
      icon: "bi-currency-dollar",
      color: "success",
      reports: [
        {
          id: "cost-analysis",
          name: "Cost Analysis",
          icon: "bi-graph-up",
          path: "cost-analysis",
        },
        // Only include Invoice Aging if invoice enhancements are enabled
        ...(invoiceEnhancementsEnabled
          ? [
              {
                id: "invoice-aging",
                name: "Invoice Aging",
                icon: "bi-clock-history",
                path: "invoice-aging",
              },
            ]
          : []),
        {
          id: "revenue-forecast",
          name: "Revenue Forecast",
          icon: "bi-graph-up-arrow",
          path: "revenue-forecast",
        },
        {
          id: "job-coach-profitability",
          name: "Job Coach Profitability",
          icon: "bi-person-badge",
          path: "job-coach-profitability",
        },
      ],
    },
    {
      id: "operational",
      name: "Operational Reports",
      icon: "bi-gear",
      color: "primary",
      reports: [
        {
          id: "auth-utilization",
          name: "Authorization Utilization",
          icon: "bi-pie-chart",
          path: "auth-utilization",
        },
        {
          id: "service-delivery",
          name: "Service Delivery",
          icon: "bi-list-check",
          path: "service-delivery",
        },
        {
          id: "consumer-activity",
          name: "Consumer Activity",
          icon: "bi-people",
          path: "consumer-activity",
        },
        {
          id: "monthly-liaison",
          name: "Monthly Liaison Report",
          icon: "bi-clipboard-data",
          path: "monthly-liaison",
        },
        {
          id: "travel-area-analysis",
          name: "Travel Area Analysis",
          icon: "bi-geo-alt",
          path: "travel-area-analysis",
        },
      ],
    },
    {
      id: "compliance",
      name: "Compliance Reports",
      icon: "bi-shield-check",
      color: "warning",
      reports: [
        {
          id: "monthly-report-compliance",
          name: "Monthly Report Compliance",
          icon: "bi-calendar-check",
          path: "monthly-report-compliance",
        },
        {
          id: "documentation-compliance",
          name: "Documentation Compliance",
          icon: "bi-file-earmark-check",
          path: "documentation-compliance",
        },
      ],
    },
    // Administrative Reports - Only visible to primary users
    ...(isPrimary
      ? [
          {
            id: "administrative",
            name: "Administrative Reports",
            icon: "bi-building",
            color: "info",
            reports: [
              {
                id: "tenant-user-count",
                name: "Tenant User Count",
                icon: "bi-people",
                path: "tenant-user-count",
              },
            ],
          },
        ]
      : []),
  ];

  // Determine which report is active based on the current path
  const getActiveReport = () => {
    const path = location.pathname;
    for (const category of reportCategories) {
      for (const report of category.reports) {
        if (path.includes(report.path)) {
          return { categoryId: category.id, reportId: report.id };
        }
      }
    }
    return { categoryId: "financial", reportId: "cost-analysis" };
  };

  const activeReport = getActiveReport();

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Open report info modal
  const openReportInfo = (e, reportId, category) => {
    e.preventDefault();
    e.stopPropagation();
    const info = reportInfo[reportId];
    if (info) {
      setSelectedReportInfo({ ...info, color: category.color });
    }
  };

  // If not vendor admin, show access denied
  if (!isVendor || !isAdmin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">
            <i className="bi bi-shield-exclamation me-2"></i>
            Access Restricted
          </h4>
          <p>
            Business Reports are only available to Vendor Administrators. Please
            contact your administrator if you believe you should have access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* HIPAA Notice */}
      <div className="alert alert-info mb-4 py-2">
        <i className="bi bi-shield-lock me-2"></i>
        <strong>HIPAA Notice:</strong> Report data may contain PHI. All access
        is logged for compliance purposes.
      </div>

      <div className="row">
        {/* Sidebar Navigation */}
        <div className="col-12 col-lg-3 col-xl-2 mb-4">
          <div className="card">
            <div className="card-header bg-dark text-white">
              <i className="bi bi-bar-chart-line me-2"></i>
              Business Reports
            </div>
            <div className="card-body p-0">
              <div className="accordion accordion-flush" id="reportCategories">
                {reportCategories.map((category) => (
                  <div className="accordion-item" key={category.id}>
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${
                          expandedCategory === category.id ? "" : "collapsed"
                        } py-2`}
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <i
                          className={`bi ${category.icon} me-2 text-${category.color}`}
                        ></i>
                        <span className="small fw-bold">{category.name}</span>
                      </button>
                    </h2>
                    <div
                      className={`accordion-collapse collapse ${
                        expandedCategory === category.id ? "show" : ""
                      }`}
                    >
                      <div className="accordion-body p-0">
                        <ul className="list-group list-group-flush">
                          {category.reports.map((report) => (
                            <li
                              key={report.id}
                              className={`list-group-item list-group-item-action py-2 ps-4 ${
                                activeReport.reportId === report.id
                                  ? `bg-${category.color} bg-opacity-10 border-start border-3 border-${category.color}`
                                  : ""
                              }`}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <Link
                                  to={`/crm/reports/${report.path}`}
                                  className={`text-decoration-none small flex-grow-1 ${
                                    activeReport.reportId === report.id
                                      ? `text-${category.color} fw-bold`
                                      : "text-dark"
                                  }`}
                                >
                                  <i className={`bi ${report.icon} me-2`}></i>
                                  {report.name}
                                </Link>
                                <button
                                  className="btn btn-link btn-sm p-0 ms-2"
                                  onClick={(e) =>
                                    openReportInfo(e, report.id, category)
                                  }
                                  title={`About ${report.name}`}
                                >
                                  <i className="bi bi-info-circle text-muted"></i>
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="col-12 col-lg-9 col-xl-10">
          <Routes>
            <Route index element={<Navigate to="cost-analysis" replace />} />

            {/* Financial Reports */}
            <Route path="cost-analysis" element={<CostAnalysisReport />} />
            {/* Invoice Aging - only available if invoice enhancements are enabled */}
            {invoiceEnhancementsEnabled ? (
              <Route path="invoice-aging" element={<InvoiceAgingReport />} />
            ) : (
              <Route path="invoice-aging" element={<Navigate to="cost-analysis" replace />} />
            )}
            <Route
              path="revenue-forecast"
              element={<RevenueForecastReport />}
            />
            <Route
              path="job-coach-profitability"
              element={<JobCoachProfitabilityReport />}
            />

            {/* Operational Reports */}
            <Route
              path="auth-utilization"
              element={<AuthorizationUtilizationReport />}
            />
            <Route
              path="service-delivery"
              element={<ServiceDeliveryReport />}
            />
            <Route
              path="consumer-activity"
              element={<ConsumerActivityReport />}
            />
            <Route
              path="monthly-liaison"
              element={<MonthlyLiaisonReport />}
            />
            <Route
              path="travel-area-analysis"
              element={<TravelAreaAnalysisReport />}
            />

            {/* Compliance Reports */}
            <Route
              path="monthly-report-compliance"
              element={<MonthlyReportComplianceReport />}
            />
            <Route
              path="documentation-compliance"
              element={<DocumentationComplianceReport />}
            />

            {/* Administrative Reports (Primary Users Only) */}
            <Route
              path="tenant-user-count"
              element={<TenantUserCountReport />}
            />
          </Routes>
        </div>
      </div>

      {/* Report Info Modal */}
      {selectedReportInfo && (
        <ReportInfoModal
          report={selectedReportInfo}
          onClose={() => setSelectedReportInfo(null)}
        />
      )}
    </div>
  );
};

export default BusinessReportsPage;